import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, ShieldCheck, ExternalLink, Building2, CheckCircle, AlertCircle, XCircle, RefreshCw, Phone, Activity, UserCheck, Lock, Zap, LogIn } from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useWhatsAppIntegration } from '../hooks/useWhatsAppIntegration';
import { whatsappService } from '../services/whatsappService';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/Layout/ConfirmationModal';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  padding: ${({ theme }) => `clamp(${theme.spacing[5]}, 3vw, ${theme.spacing[8]})`};
  gap: ${({ theme }) => `clamp(${theme.spacing[5]}, 3vw, ${theme.spacing[8]})`};
  background: ${props => props.theme.colors.background};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const BackButton = styled.button`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  padding: ${props => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[1]};

  h1 {
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    color: ${props => props.theme.colors.textPrimary};
  }

  p {
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 2rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.xl}) {
    gap: 1.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    gap: 1rem;
    flex-direction: column;
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: ${props => props.theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
  flex: 1 1 calc(33.333% - 1.5rem);
  min-width: 320px;
  max-width: 550px;

  @media (max-width: ${props => props.theme.breakpoints.xl}) {
    flex: 1 1 calc(50% - 1rem);
    max-width: 100%;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex: 1 1 100%;
    min-width: 100%;
  }
`;

const PrimaryCard = styled(Card)`
  border: 1px solid ${props => `${props.theme.colors.primary}30`};
  background: ${props => props.theme.colors.backgroundSecondary};
  gap: ${props => props.theme.spacing[5]};
  flex: 1 1 100%;
  max-width: 100%;
`;

const PrimaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  width: fit-content;
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.full};
  background: ${props => props.theme.isLight ? `${props.theme.colors.primary}1a` : `${props.theme.colors.primary}22`};
  font-size: ${props => props.theme.typography.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${props => props.theme.colors.primaryLight};
`;

const PrimaryTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};

  h2 {
    font-size: ${({ theme }) => `clamp(${theme.typography.fontSize.xl}, 3vw, ${theme.typography.fontSize['3xl']})`};
    line-height: ${props => props.theme.typography.lineHeight.tight};
    margin: 0;
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const PrimaryDescription = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  max-width: 48ch;
  line-height: ${props => props.theme.typography.lineHeight.normal};
`;

const PrimaryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borders.radius.full};
  border: 1px solid ${props => props.theme.colors.borderLight};
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    width: 100%;
    justify-content: center;
  }
`;

const HighlightsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
`;

const HighlightChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.full};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
`;

const ChipIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: ${props => `${props.theme.colors.primary}22`};
  color: ${props => props.theme.colors.primary};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};

  h2 {
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize.lg};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    color: ${props => props.theme.colors.textPrimary};
  }

  p {
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize.xs};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.primaryDark} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing[3]};
  justify-content: center;
  align-items: stretch;
  flex: 1;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const StepItem = styled.div`
  padding: ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.borderLight};
  background: ${props => props.theme.colors.backgroundTertiary};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  box-shadow: ${props => props.theme.isLight ? props.theme.shadows.base : props.theme.shadows.sm};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.easeInOut};
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary}40;
  }
`;

const StepBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}22 0%, ${props => props.theme.colors.primary}11 100%);
  border: 2px solid ${props => props.theme.colors.primary}60;
  color: ${props => props.theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StepTitle = styled.strong`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textPrimary};
`;

const StepDescription = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  align-self: flex-start;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[5]};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.primaryHover} 100%
  );
  border: none;
  border-radius: ${props => props.theme.borders.radius.full};
  color: #ffffff;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  font-size: ${props => props.theme.typography.fontSize.sm};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 30px rgba(255, 59, 48, 0.25);
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    width: 100%;
    justify-content: center;
  }
`;

const StepsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing[3]};
  justify-content: center;
  align-items: stretch;
  flex: 1;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ListItem = styled.div`
  padding: ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.borderLight};
  background: ${props => props.theme.colors.backgroundTertiary};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  box-shadow: ${props => props.theme.isLight ? props.theme.shadows.base : props.theme.shadows.sm};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.easeInOut};
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary}40;
  }
`;

const ListIconBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}22 0%, ${props => props.theme.colors.primary}11 100%);
  border: 2px solid ${props => props.theme.colors.primary}60;
  color: ${props => props.theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ListItemTitle = styled.strong`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
`;

const ListItemDescription = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeight.normal};
`;

const BusinessList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${props => props.theme.spacing[3]};
  justify-content: center;
  align-items: stretch;
  flex: 1;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const BusinessItem = styled.div`
  padding: ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.borderLight};
  background: ${props => props.theme.colors.backgroundTertiary};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  box-shadow: ${props => props.theme.isLight ? props.theme.shadows.base : props.theme.shadows.sm};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.easeInOut};
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary}40;
  }
`;

const BusinessItemBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}22 0%, ${props => props.theme.colors.primary}11 100%);
  border: 2px solid ${props => props.theme.colors.primary}60;
  color: ${props => props.theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BusinessItemTitle = styled.strong`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textPrimary};
`;

const BusinessItemDescription = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeight.normal};
`;

// Status Card Components
const StatusGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const StatusCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius['2xl']};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  padding: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const IconBadge = styled.div<{ $variant?: 'active' | 'warning' | 'danger' }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $variant }) => {
    if ($variant === 'active') return `${theme.colors.success}15`;
    if ($variant === 'warning') return `${theme.colors.warning}15`;
    return `${theme.colors.error}15`;
  }};
  border: 1px solid ${({ theme, $variant }) => {
    if ($variant === 'active') return `${theme.colors.success}60`;
    if ($variant === 'warning') return `${theme.colors.warning}60`;
    return `${theme.colors.error}60`;
  }};
  color: ${({ theme, $variant }) => {
    if ($variant === 'active') return theme.colors.success;
    if ($variant === 'warning') return theme.colors.warning;
    return theme.colors.error;
  }};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatusContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StatusText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StatusSubtext = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusBadge = styled.span<{ $variant: 'success' | 'warning' | 'danger' }>`
  align-self: flex-start;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ theme, $variant }) => {
    if ($variant === 'success') return `${theme.colors.success}20`;
    if ($variant === 'warning') return `${theme.colors.warning}20`;
    return `${theme.colors.error}20`;
  }};
  color: ${({ theme, $variant }) => {
    if ($variant === 'success') return theme.colors.success;
    if ($variant === 'warning') return theme.colors.warning;
    return theme.colors.error;
  }};
  border: 1px solid ${({ theme, $variant }) => {
    if ($variant === 'success') return `${theme.colors.success}50`;
    if ($variant === 'warning') return `${theme.colors.warning}50`;
    return `${theme.colors.error}50`;
  }};
`;

const StatusDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  padding-top: ${({ theme }) => theme.spacing[2]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatusActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ theme, $variant }) => {
    if ($variant === 'primary') return theme.colors.primary;
    if ($variant === 'danger') return theme.colors.error;
    return theme.colors.border;
  }};
  background: ${({ theme, $variant }) => {
    if ($variant === 'primary') return theme.colors.primary;
    if ($variant === 'danger') return `${theme.colors.error}15`;
    return 'transparent';
  }};
  color: ${({ theme, $variant }) => {
    if ($variant === 'primary') return '#ffffff';
    if ($variant === 'danger') return theme.colors.error;
    return theme.colors.textSecondary;
  }};

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  svg.spinning {
    animation: spin 0.9s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }

  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }
`;

const QualityBadge = styled.span<{ $rating?: 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ theme, $rating }) => {
    if ($rating === 'GREEN') return `${theme.colors.success}20`;
    if ($rating === 'YELLOW') return `${theme.colors.warning}20`;
    if ($rating === 'RED') return `${theme.colors.error}20`;
    return `${theme.colors.textTertiary}20`;
  }};
  color: ${({ theme, $rating }) => {
    if ($rating === 'GREEN') return theme.colors.success;
    if ($rating === 'YELLOW') return theme.colors.warning;
    if ($rating === 'RED') return theme.colors.error;
    return theme.colors.textTertiary;
  }};
  border: 1px solid ${({ theme, $rating }) => {
    if ($rating === 'GREEN') return `${theme.colors.success}50`;
    if ($rating === 'YELLOW') return `${theme.colors.warning}50`;
    if ($rating === 'RED') return `${theme.colors.error}50`;
    return `${theme.colors.textTertiary}50`;
  }};
`;

const WhatsAppIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user: googleUser } = useCurrentUser();

  // Estado do modal de confirmação
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

  // WhatsApp Integration Status
  const {
    hasIntegration,
    isActive,
    phoneNumber,
    phoneNumberId,
    whatsappAccountId,
    verifiedName,
    qualityRating,
    lastError,
    integrationStatus,
    isLoadingStatus,
    isFetching,
    refetchStatus,
    disconnectAccount,
    isDisconnecting,
  } = useWhatsAppIntegration(googleUser?.uid);

  // Debug temporário
  React.useEffect(() => {
    if (hasIntegration) {
      console.log('WhatsApp Integration Status:', {
        hasIntegration,
        isActive,
        phoneNumber,
        phoneNumberId,
        whatsappAccountId,
        verifiedName,
        qualityRating,
        lastError,
        integrationStatus,
      });
    }
  }, [hasIntegration, isActive, phoneNumber, phoneNumberId, whatsappAccountId, verifiedName, qualityRating, lastError, integrationStatus]);

  // Lógica de variante do status
  const statusVariant = useMemo<'success' | 'warning' | 'danger'>(() => {
    if (!hasIntegration) return 'danger';
    if (lastError) return 'warning';
    // Se tem integração e número de telefone, considera conectado
    if (hasIntegration && phoneNumber) return 'success';
    if (isActive) return 'success';
    return 'warning';
  }, [hasIntegration, isActive, lastError, phoneNumber]);

  const statusText = useMemo(() => {
    if (!hasIntegration) return 'Desconectado';
    if (lastError) return 'Com problemas';
    // Se tem integração e número de telefone, considera conectado
    if (hasIntegration && phoneNumber) return 'Conectado';
    if (isActive) return 'Conectado';
    return 'Aguardando validação';
  }, [hasIntegration, isActive, lastError, phoneNumber]);

  const statusIcon = useMemo(() => {
    if (!hasIntegration) return XCircle;
    if (lastError) return AlertCircle;
    // Se tem integração e número de telefone, mostra check
    if (hasIntegration && phoneNumber) return CheckCircle;
    if (isActive) return CheckCircle;
    return AlertCircle;
  }, [hasIntegration, isActive, lastError, phoneNumber]);

  const StatusIcon = statusIcon;

  const handleBack = () => {
    navigate('/home');
  };

  const handleRefresh = () => {
    refetchStatus();
    toast.success('Status atualizado!');
  };

  const handleDisconnect = () => {
    setIsDisconnectModalOpen(true);
  };

  const handleConfirmDisconnect = () => {
    disconnectAccount();
    setIsDisconnectModalOpen(false);
  };

  const handleReauthorize = () => {
    // Reusa o fluxo OAuth existente
    handleStartSetup();
  };

  const handleStartSetup = async () => {
    if (!googleUser?.uid) {
      toast.error('Faça login com Google para iniciar a configuração.');
      return;
    }

    try {
      toast.loading('Gerando URL de autenticação...', { id: 'oauth-url' });

      const response = await whatsappService.getOAuthUrl(googleUser.uid);

      if (response.success && response.url) {
        // Valida se é URL do Facebook
        if (!response.url.includes('facebook.com')) {
          toast.error('URL OAuth inválida', { id: 'oauth-url' });
          return;
        }

        // Remove o toast loading antes de redirecionar
        toast.dismiss('oauth-url');

        // Redireciona para Facebook OAuth
        window.location.href = response.url;
      } else {
        toast.error('Erro ao gerar URL de autenticação', { id: 'oauth-url' });
      }
    } catch (error: any) {
      console.error('Erro ao iniciar setup:', error);
      toast.error('Erro ao iniciar configuração', { id: 'oauth-url' });
    }
  };

  const handleOpenDocs = () => {
    window.open('https://www.facebook.com/business/help/2058515294227817', '_blank', 'noopener,noreferrer');
  };

  const onboardingSteps = [
    {
      icon: LogIn,
      title: 'Autenticação',
      description: 'Entre com a conta que gerencia o Business Manager.',
    },
    {
      icon: Building2,
      title: 'Negócio',
      description: 'Escolha o Business Manager que possui o número oficial.',
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Selecione o número e conceda as permissões solicitadas.',
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={18} />
        </BackButton>
        <HeaderTitle>
          <h1>Integração WhatsApp Business</h1>
          <p>Configure sua conta de forma rápida e segura.</p>
        </HeaderTitle>
      </PageHeader>

      {/* Status Cards */}
      {!isLoadingStatus && (
        <StatusGrid>
          {/* Card 1: Status da Conexão */}
          <StatusCard>
            <StatusHeader>
              <IconBadge $variant={statusVariant === 'success' ? 'active' : statusVariant}>
                <StatusIcon />
              </IconBadge>
              <StatusContent>
                <StatusText>Status da Conexão</StatusText>
                <StatusBadge $variant={statusVariant}>{statusText}</StatusBadge>
              </StatusContent>
            </StatusHeader>

            {hasIntegration && phoneNumber && (
              <>
                <InfoRow>
                  <Phone />
                  <span>
                    Número: <strong>{phoneNumber}</strong>
                  </span>
                </InfoRow>
                {verifiedName && (
                  <InfoRow>
                    <CheckCircle />
                    <span>
                      Nome Verificado: <strong>{verifiedName}</strong>
                    </span>
                  </InfoRow>
                )}
                {qualityRating && (
                  <InfoRow>
                    <Activity />
                    <span>Qualidade da Conta:</span>
                    <QualityBadge $rating={qualityRating}>
                      {qualityRating === 'GREEN' && '✓ Boa'}
                      {qualityRating === 'YELLOW' && '⚠ Média'}
                      {qualityRating === 'RED' && '✗ Baixa'}
                      {qualityRating === 'UNKNOWN' && '? Desconhecida'}
                    </QualityBadge>
                  </InfoRow>
                )}
              </>
            )}

            {!hasIntegration && (
              <StatusDescription>
                Nenhuma conta WhatsApp Business conectada. Clique em "Iniciar integração" abaixo para conectar sua conta.
              </StatusDescription>
            )}

            {lastError && (
              <StatusDescription style={{ color: 'var(--color-error)' }}>
                <strong>Erro:</strong> {lastError}
              </StatusDescription>
            )}

            {hasIntegration && (
              <Actions>
                <StatusActionButton $variant="ghost" onClick={handleRefresh} disabled={isFetching}>
                  <RefreshCw className={isFetching ? 'spinning' : ''} />
                  {isFetching ? 'Atualizando...' : 'Atualizar'}
                </StatusActionButton>
                {lastError && (
                  <StatusActionButton $variant="primary" onClick={handleReauthorize}>
                    <ExternalLink />
                    Reautorizar
                  </StatusActionButton>
                )}
                <StatusActionButton
                  $variant="danger"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                >
                  <XCircle />
                  {isDisconnecting ? 'Desconectando...' : 'Desconectar'}
                </StatusActionButton>
              </Actions>
            )}
          </StatusCard>

          {/* Card 2: Informações da Conta */}
          <StatusCard>
            <StatusHeader>
              <IconBadge $variant={hasIntegration ? 'active' : 'danger'}>
                <Building2 />
              </IconBadge>
              <StatusContent>
                <StatusText>Conta WhatsApp Business</StatusText>
                <StatusSubtext>
                  {hasIntegration ? 'Integração ativa' : 'Aguardando conexão'}
                </StatusSubtext>
              </StatusContent>
            </StatusHeader>

            {hasIntegration && whatsappAccountId ? (
              <>
                <InfoRow>
                  <Activity />
                  <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                    WABA ID: <strong>{whatsappAccountId}</strong>
                  </span>
                </InfoRow>
                {phoneNumberId && (
                  <InfoRow>
                    <Phone />
                    <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                      Phone ID: <strong>{phoneNumberId}</strong>
                    </span>
                  </InfoRow>
                )}
                <StatusDescription>
                  Sua conta WhatsApp Business está conectada e pronta para automações via Loopra.
                </StatusDescription>
              </>
            ) : (
              <StatusDescription>
                Conecte sua conta WhatsApp Business para visualizar informações detalhadas da integração.
              </StatusDescription>
            )}
          </StatusCard>
        </StatusGrid>
      )}

      <Content>
        <PrimaryCard>
          <PrimaryBadge>Integração oficial</PrimaryBadge>
          <PrimaryTitle>
            <h2>Conecte seu WhatsApp Business em minutos</h2>
            <PrimaryDescription>
              Use sua conta do Facebook Business Manager para liberar automações e centralizar conversas no Loopra.
            </PrimaryDescription>
          </PrimaryTitle>

          <HighlightsRow>
            <HighlightChip>
              <ChipIcon>
                <ShieldCheck size={12} />
              </ChipIcon>
              Autenticação segura
            </HighlightChip>
            <HighlightChip>
              <ChipIcon>
                <Building2 size={12} />
              </ChipIcon>
              Seleção guiada do negócio
            </HighlightChip>
          </HighlightsRow>

          <PrimaryActions>
            <ActionButton onClick={handleStartSetup}>
              Iniciar integração
              <ExternalLink size={16} />
            </ActionButton>
            <SecondaryButton onClick={handleOpenDocs}>
              Ver guia rápido
            </SecondaryButton>
          </PrimaryActions>
        </PrimaryCard>

        <Card>
          <CardHeader>
            <IconWrapper>
              <MessageCircle />
            </IconWrapper>
            <div>
              <h2>Fluxo de integração</h2>
              <p>Acompanhe as etapas principais para conectar seu WhatsApp Business.</p>
            </div>
          </CardHeader>

          <StepsGrid>
            {onboardingSteps.map((step) => {
              const IconComponent = step.icon;
              return (
                <StepItem key={step.title}>
                  <StepBadge>
                    <IconComponent />
                  </StepBadge>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepItem>
              );
            })}
          </StepsGrid>
        </Card>

        <Card>
          <CardHeader>
            <IconWrapper>
              <Building2 />
            </IconWrapper>
            <div>
              <h2>Selecione seu Negócio</h2>
              <p>Escolha qual Business Manager conectar ao WhatsApp dentro do fluxo.</p>
            </div>
          </CardHeader>

          <BusinessList>
            <BusinessItem>
              <BusinessItemBadge>
                <Building2 />
              </BusinessItemBadge>
              <BusinessItemTitle>Escolha do Negócio</BusinessItemTitle>
              <BusinessItemDescription>
                Confirme o Business Manager que já contém o número WhatsApp Business.
              </BusinessItemDescription>
            </BusinessItem>

            <BusinessItem>
              <BusinessItemBadge>
                <UserCheck />
              </BusinessItemBadge>
              <BusinessItemTitle>Conta Administradora</BusinessItemTitle>
              <BusinessItemDescription>
                Use uma conta com papel de administrador e permissões de marketing e atendimento.
              </BusinessItemDescription>
            </BusinessItem>

            <BusinessItem>
              <BusinessItemBadge>
                <ShieldCheck />
              </BusinessItemBadge>
              <BusinessItemTitle>Autorização de Acesso</BusinessItemTitle>
              <BusinessItemDescription>
                Conceda as permissões solicitadas para o Loopra sincronizar conversas automaticamente.
              </BusinessItemDescription>
            </BusinessItem>

            <BusinessItem>
              <BusinessItemBadge>
                <Activity />
              </BusinessItemBadge>
              <BusinessItemTitle>Verificação Automática</BusinessItemTitle>
              <BusinessItemDescription>
                Aguarde enquanto o sistema valida sua integração e ativa o bot de atendimento.
              </BusinessItemDescription>
            </BusinessItem>
          </BusinessList>
        </Card>

      </Content>

      {/* Modal de Confirmação de Desconexão */}
      <ConfirmationModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleConfirmDisconnect}
        title="Desconectar WhatsApp Business"
        message={
          <>
            Tem certeza que deseja desconectar sua conta WhatsApp Business do Loopra?
            <br />
            <br />
            Esta ação removerá a integração atual e você precisará refazer o processo
            de autenticação caso queira reconectar.
            {phoneNumber && (
              <>
                <br />
                <br />
                <strong>Número a ser desconectado: {phoneNumber}</strong>
              </>
            )}
          </>
        }
        confirmText="Desconectar"
        cancelText="Cancelar"
        loading={isDisconnecting}
      />
    </PageContainer>
  );
};

export default WhatsAppIntegration;
